// ── CASH LEDGER ─────────────────────────────────────────────────────────────
// The SINGLE source of truth for cash movement. Instead of two disagreeing
// balance engines and cash recorded in three overlapping places, this module
// DERIVES one flat, dated ledger of cash movements from the business records,
// and both the dashboard balances and the per-wallet balances are computed from
// it. Because every row is dated, it also answers "cash received in a period".
//
// Design rule — ONE row per real cash movement, taken from its single source:
//   • sale / purchase / expense cash   → the record's non-credit payments[]
//   • manual-invoice deposits          → the invoice's non-credit payments[]
//   • customer / supplier refunds      → the return's cashRefundAmt
//   • opening balances                 → each payment account
//   • genuine wallet adjustments       → manualAdjustments (top-ups,
//     withdrawals, drawings, transfers) — NOT collection/return/reversal
//     "bridge" entries, which are already captured by their source above.
//
// Pure: no React, no storage, no side effects. Locked down by cashLedger.test.js.

// ── Enumerations ────────────────────────────────────────────────────────────
// Use these instead of bare string literals so new movement types (wallet
// transfers, owner drawings/contributions, bank charges, interest…) are added
// in one place and typos can't silently create a new category.
export const KIND = Object.freeze({
  SALE: 'sale',
  PURCHASE: 'purchase',
  EXPENSE: 'expense',
  MANUAL: 'manual',
});

export const ACTION = Object.freeze({
  PAYMENT: 'payment',
  REFUND: 'refund',
  ADJUSTMENT: 'adjustment',
  OPENING: 'opening',
  TRANSFER: 'transfer',
});

export const SOURCE = Object.freeze({
  OPENING: 'opening',
  SALE: 'sale',
  MANUAL_INVOICE: 'manualInvoice',
  PURCHASE: 'purchase',
  EXPENSE: 'expense',
  SERVICE: 'service',
  SALES_RETURN: 'salesReturn',
  PURCHASE_RETURN: 'purchaseReturn',
  MANUAL: 'manual',
  WALLET_TRANSFER: 'walletTransfer',
});

export const baseType = (v) => (typeof v === 'string' ? v.split('|')[0] : (v || 'cash'));

// Is this manual transaction a "bridge" — a cash movement the ledger already
// derives from a source record (a payment line, a return's cashRefundAmt, or a
// live record for reversals)? Bridges MUST be excluded from the ledger's manual
// adjustments or money double-counts.
//   PRIMARY  : an explicit `systemGenerated` flag set at creation (robust — no
//              dependence on wording, localization, typos, or user edits).
//   FALLBACK : description match, only for LEGACY rows created before the flag.
// Genuine adjustments (top-ups, withdrawals, drawings, capital) return false.
export const isBridgeManualTransaction = (mt) => {
  if (!mt) return false;
  if (mt.systemGenerated === true) return true;
  return /Reversal|Payment (received|adjustment|deleted)|Malipo yamepokelewa|Sales Return|Purchase Return|Rudisha Manunuzi|Bidhaa iliyorudishwa|Cashflow adjustment/i.test(String(mt.description || ''));
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Non-credit payment lines on a record, or null if it has no payments[] array.
const nonCreditPayments = (rec) =>
  Array.isArray(rec && rec.payments) && rec.payments.length > 0
    ? rec.payments.filter((p) => (p.type || p.method) !== 'credit')
    : null;

// Build the flat, dated cash ledger. Each row:
//   { date, accountType, accountKey, direction:'in'|'out', amount, source, refId }
export function buildCashLedger({
  paymentAccounts = [],
  sales = [],
  purchases = [],
  expenses = [],
  serviceTransactions = [],
  manualInvoices = [],
  salesReturns = [],
  purchaseReturns = [],
  manualAdjustments = [],
} = {}) {
  const rows = [];
  // meta: { source, kind, sourceAction, refId }
  //   kind         : 'sale' | 'purchase' | 'expense' | 'manual'
  //   sourceAction : 'payment' | 'refund' | 'adjustment'
  const add = (date, accountKey, direction, amount, meta = {}) => {
    const amt = num(amount);
    if (amt === 0) return;
    const key = accountKey || 'cash';
    if (baseType(key) === 'none') return; // non-cash (e.g. stock write-off)
    rows.push({
      date: date || '',
      wallet: baseType(key),        // broad wallet: cash | bank | mobilemoney
      accountType: baseType(key),   // alias kept for existing readers
      accountKey: key,              // composite (e.g. 'bank|<id>')
      direction,                    // 'in' | 'out'
      amount: Math.abs(amt),
      kind: meta.kind || 'manual',
      sourceAction: meta.sourceAction || 'payment',
      source: meta.source,          // fine-grained origin, for debugging
      refId: meta.refId,
    });
  };

  // Opening balances — cash already in each wallet at the start.
  paymentAccounts.forEach((a) => {
    const key = a.type === 'cash' ? 'cash' : `${a.type}|${a.id}`;
    add(a.openingBalanceDate, key, 'in', a.openingBalance, { source: SOURCE.OPENING, kind: KIND.MANUAL, sourceAction: ACTION.OPENING, refId: a.id });
  });

  // Sales — cash IN (non-credit payment lines; legacy fallback to paymentMethod).
  sales.forEach((s) => {
    const pmts = nonCreditPayments(s);
    if (pmts) {
      pmts.forEach((p) => add(p.date || s.date, p.accountKey || p.type || 'cash', 'in', p.amount, { source: SOURCE.SALE, kind: KIND.SALE, sourceAction: ACTION.PAYMENT, refId: s.id }));
      // Heal old flag-only debt collections: a sale marked PAID whose recorded
      // cash is less than what was owed had its balance collected the lossy way
      // (the old handler flipped isPaid without recording a payment line). Add
      // the shortfall on the wallet it was booked against so the ledger sees it.
      if (s.isPaid === true) {
        const cashSum = pmts.reduce((a, p) => a + num(p.amount), 0);
        const missing = num(s.totalRevenue) - num(s.creditReducedByReturns) - cashSum;
        if (missing > 0.005) add(s.paidDate || s.date, s.accountKey || s.paymentMethod || 'cash', 'in', missing, { source: SOURCE.SALE, kind: KIND.SALE, sourceAction: ACTION.PAYMENT, refId: s.id });
      }
    } else if (s.isPaid !== false) {
      add(s.date, s.accountKey || s.paymentMethod || 'cash', 'in', s.totalRevenue, { source: SOURCE.SALE, kind: KIND.SALE, sourceAction: ACTION.PAYMENT, refId: s.id });
    }
  });

  // Manual invoices — deposits taken before the invoice becomes a real sale.
  manualInvoices.forEach((mi) => {
    const pmts = nonCreditPayments(mi);
    if (pmts) pmts.forEach((p) => add(p.date || mi.date, p.accountKey || p.type || 'cash', 'in', p.amount, { source: SOURCE.MANUAL_INVOICE, kind: KIND.SALE, sourceAction: ACTION.PAYMENT, refId: mi.id }));
  });

  // Purchases — cash OUT.
  purchases.forEach((p) => {
    const pmts = nonCreditPayments(p);
    if (pmts) {
      pmts.forEach((x) => add(x.date || p.date, x.accountKey || x.type || 'cash', 'out', x.amount, { source: SOURCE.PURCHASE, kind: KIND.PURCHASE, sourceAction: ACTION.PAYMENT, refId: p.id }));
      // Heal old flag-only supplier settlements (same logic as sales).
      if (p.isPaid === true) {
        const cashSum = pmts.reduce((a, x) => a + num(x.amount), 0);
        const missing = num(p.totalCost) - num(p.payableReducedByReturns) - cashSum;
        if (missing > 0.005) add(p.paidDate || p.date, p.accountKey || p.paymentMethod || 'cash', 'out', missing, { source: SOURCE.PURCHASE, kind: KIND.PURCHASE, sourceAction: ACTION.PAYMENT, refId: p.id });
      }
    } else if (p.isPaid !== false) {
      add(p.date, p.accountKey || p.paymentMethod || 'cash', 'out', p.totalCost, { source: SOURCE.PURCHASE, kind: KIND.PURCHASE, sourceAction: ACTION.PAYMENT, refId: p.id });
    }
  });

  // Expenses — cash OUT. Non-cash write-offs (method 'none') never touch a wallet.
  expenses.forEach((e) => {
    if (e.paymentMethod === 'none' || e.isStockWriteoff) return;
    const pmts = nonCreditPayments(e);
    if (pmts) pmts.forEach((x) => add(x.date || e.date, x.accountKey || x.type || 'cash', 'out', x.amount, { source: SOURCE.EXPENSE, kind: KIND.EXPENSE, sourceAction: ACTION.PAYMENT, refId: e.id }));
    else if (e.isPaid !== false) add(e.date, e.accountKey || e.paymentMethod || 'cash', 'out', e.amount, { source: SOURCE.EXPENSE, kind: KIND.EXPENSE, sourceAction: ACTION.PAYMENT, refId: e.id });
  });

  // Service transactions — cash IN. Services support partial/credit exactly
  // like sales (payments[] with credit lines + isPaid), so use the same rule:
  // count only non-credit payment lines; fall back to the full amount only for
  // legacy fully-paid records that have no payments[].
  serviceTransactions.forEach((st) => {
    const pmts = nonCreditPayments(st);
    if (pmts) pmts.forEach((p) => add(p.date || st.date, p.accountKey || p.type || 'cash', 'in', p.amount, { source: SOURCE.SERVICE, kind: KIND.SALE, sourceAction: ACTION.PAYMENT, refId: st.id }));
    else if (st.isPaid !== false) add(st.date, st.accountKey || st.paymentMethod || 'cash', 'in', st.totalRevenue != null ? st.totalRevenue : st.price, { source: SOURCE.SERVICE, kind: KIND.SALE, sourceAction: ACTION.PAYMENT, refId: st.id });
  });

  // Customer refunds — cash OUT (only the cash portion).
  salesReturns.forEach((r) => add(r.date, r.refundAccountKey || r.refundMethod || 'cash', 'out', r.cashRefundAmt, { source: SOURCE.SALES_RETURN, kind: KIND.SALE, sourceAction: ACTION.REFUND, refId: r.id }));

  // Supplier refunds — cash IN (only the cash portion).
  purchaseReturns.forEach((r) => add(r.date, r.refundAccountKey || r.refundMethod || 'cash', 'in', r.cashRefundAmt, { source: SOURCE.PURCHASE_RETURN, kind: KIND.PURCHASE, sourceAction: ACTION.REFUND, refId: r.id }));

  // Genuine manual wallet adjustments (top-ups, withdrawals, drawings, transfers).
  manualAdjustments.forEach((m) => add(m.date, m.accountKey || m.account || 'cash', m.type === 'credit' ? 'in' : 'out', m.amount, { source: m.transfer ? SOURCE.WALLET_TRANSFER : SOURCE.MANUAL, kind: KIND.MANUAL, sourceAction: m.transfer ? ACTION.TRANSFER : ACTION.ADJUSTMENT, refId: m.id }));

  return rows;
}

// ── TRANSFER PAIR INTEGRITY ────────────────────────────────────────────────
// A wallet transfer is a PAIR of legs sharing a transferId: exactly one debit
// (out of one wallet) and one credit (into another), equal in amount. Anything
// else is a broken transfer — a lone leg (e.g. a crash/partial sync wrote only
// one side), a leg with no transferId (can't be paired), or an unbalanced pair.
// Surface them so a half-written transfer can never silently distort balances.
// Returns one row per bad group: { transferId, reason, legIds, inAmount, outAmount }.
//   reason: 'missing_transfer_id' | 'incomplete_pair' | 'unbalanced_pair'
export function brokenTransfers(manualAdjustments = []) {
  const groups = {};
  (manualAdjustments || []).forEach((m) => {
    if (!m || !m.transfer || m.deleted) return;
    const hasId = m.transferId != null && m.transferId !== '';
    const key = hasId ? `id:${m.transferId}` : `noid:${m.id}`;
    (groups[key] = groups[key] || []).push(m);
  });
  const out = [];
  Object.keys(groups).forEach((key) => {
    const legs = groups[key];
    const noId = key.startsWith('noid:');
    const inLegs = legs.filter((m) => m.type === 'credit');
    const outLegs = legs.filter((m) => m.type !== 'credit');
    const inAmount = inLegs.reduce((s, m) => s + num(m.amount), 0);
    const outAmount = outLegs.reduce((s, m) => s + num(m.amount), 0);
    let reason = null;
    if (noId) reason = 'missing_transfer_id';
    else if (legs.length !== 2 || inLegs.length !== 1 || outLegs.length !== 1) reason = 'incomplete_pair';
    else if (Math.abs(inAmount - outAmount) > 0.0001) reason = 'unbalanced_pair';
    if (reason) out.push({ transferId: noId ? null : key.slice(3), reason, legIds: legs.map((m) => m.id), inAmount, outAmount });
  });
  return out;
}

// Does a ledger row belong to the queried wallet? Supports composite keys
// ('bank|<id>') and legacy bare types ('bank' matches any bank account).
export function rowMatchesAccount(row, accountKey) {
  if (!accountKey) return false;
  if (row.accountKey === accountKey) return true;
  if (baseType(accountKey) === accountKey && row.accountType === accountKey) return true;
  return false;
}

// Balance of one wallet (opening + ins − outs).
export function walletBalance(ledger, accountKey) {
  if (!accountKey || accountKey === 'credit') return 0;
  return ledger.reduce((bal, r) => {
    if (!rowMatchesAccount(r, accountKey)) return bal;
    return bal + (r.direction === 'in' ? r.amount : -r.amount);
  }, 0);
}

// Aggregate balances by broad type — for the dashboard Cash Position.
export function aggregateBalances(ledger) {
  const b = { cash: 0, bank: 0, mobileMoney: 0 };
  ledger.forEach((r) => {
    const sign = r.direction === 'in' ? 1 : -1;
    if (r.accountType === 'bank') b.bank += sign * r.amount;
    else if (r.accountType === 'mobilemoney') b.mobileMoney += sign * r.amount;
    else b.cash += sign * r.amount;
  });
  return b;
}

// Wallet statement for a report period. `matchFn(row)` selects the rows for the
// chosen wallet or specific sub-account. Dates are compared as 'YYYY-MM-DD'
// strings, which sort correctly and are timezone-independent.
//   opening = pre-period movements (date < start) PLUS the account's own opening
//             balance whenever it falls on/before the period end. An opening
//             balance is the account's STARTING position, not a transaction, so
//             it seeds the Opening figure and never appears as an in-period
//             movement — even when the report window starts before the date the
//             account was opened (e.g. a "Year" view of an account opened mid-year).
//   closing = rows up to and INCLUDING end    (date <= end)   ← July 31 counts
//   current = all rows to date
// Identity: closing = opening + Σ(non-opening rows where start <= date <= end).
export function walletStatement(ledger, matchFn, start, end) {
  const rows = (ledger || []).filter((r) => matchFn(r));
  const isOpening = (r) => r.sourceAction === ACTION.OPENING || r.source === SOURCE.OPENING;
  const sum = (rs) => rs.reduce((s, r) => s + (r.direction === 'in' ? r.amount : -r.amount), 0);
  return {
    // Opening rows count toward Opening as long as they're on/before the period
    // end; all other rows count toward Opening only if strictly before start.
    opening: sum(rows.filter((r) => isOpening(r) ? (!end || r.date <= end) : (!start || r.date < start))),
    closing: sum(rows.filter((r) => !end || r.date <= end)),
    current: sum(rows),
    // Movement list excludes opening-balance rows (they live in the Opening figure).
    periodRows: rows.filter((r) => !isOpening(r) && (!start || r.date >= start) && (!end || r.date <= end)),
  };
}

// Cash actually received (in) or paid (out) within a date range — the thing
// that was previously uncomputable without dated payment rows.
// Operating cash flow for a period. Openings are excluded (not a flow), and by
// default so are wallet-to-wallet TRANSFERS: an internal transfer moves money
// between your own wallets — it is neither business cash received nor cash paid
// out, so counting its legs would fabricate an equal-and-opposite in/out pair.
// Pass { includeTransfers: true } only for a raw wallet-movement view that wants
// the transfer legs (individual wallet statements/balances already include them
// via walletStatement/walletBalance, which don't use this function).
export function cashInPeriod(ledger, fromDate, toDate, direction = 'in', { includeTransfers = false } = {}) {
  return ledger.reduce((sum, r) => {
    if (r.direction !== direction) return sum;
    if (r.sourceAction === 'opening' || r.source === 'opening') return sum; // openings are not period cash flow
    if (!includeTransfers && r.sourceAction === 'transfer') return sum;      // internal transfer, not business cashflow
    if (fromDate && r.date < fromDate) return sum;
    if (toDate && r.date > toDate) return sum;
    return sum + r.amount;
  }, 0);
}
